import { produce } from "immer";
import React, { useMemo } from "react";
import { FilterState } from "../../adapters/insights-agent/state/AppState.ts";
import { Dropdown } from "../../ui-library/Dropdown/Dropdown.tsx";
import { InputLabel } from "../../ui-library/InputLabel/InputLabel.tsx";
import { MonthRangePicker } from "../../ui-library/MonthRangePicker/MonthRangePicker.tsx";
import styles from "./FilterOptions.module.css";

export interface FilterOption {
  label: string;
  key: string;
  type: "selection_multiple" | "month_range";
  custom: boolean;
  options?: {
    label: string;
    value: string;
  }[];
}

export interface FilterOptionsProps {
  filterOptions: FilterOption[];
  filterState: FilterState;
  onFilterChange: (filterState: FilterState) => void;
  loading?: boolean;
}

const useSelected = (
  filter: FilterOption,
  filterState: FilterState,
): string[] =>
  useMemo(() => {
    return filter.custom
      ? (filterState.custom_tags_filter || []).find(
          (tag) => tag.key === filter.key,
        )?.values || []
      : (filterState[filter.key as keyof FilterState] as string[]) || [];
  }, [filter.custom, filter.key, filterState]);

export const FilterOptions: React.FC<FilterOptionsProps> = (props) => {
  return (
    <div className={styles.Container} data-test={"new-filter-options"}>
      <div className={styles.FiltersGrid}>
        {props.filterOptions.map((filter) => (
          <FilterOptionItem
            key={filter.key}
            filter={filter}
            filterState={props.filterState}
            onFilterChange={props.onFilterChange}
          />
        ))}
      </div>
    </div>
  );
};

const FilterOptionItem: React.FC<{
  filter: FilterOption;
  filterState: FilterState;
  onFilterChange: (filterState: FilterState) => void;
}> = ({ onFilterChange, filter, filterState }) => {
  const selected = useSelected(filter, filterState);
  return filter.type === "month_range" ? (
    <div className={styles.FilterItem}>
      <MonthRangePicker
        label={filter.label}
        value={filterState.creation_date || {}}
        onSelect={(value) => {
          onFilterChange(
            produce(filterState, (draft) => {
              draft.creation_date = value;
            }),
          );
        }}
      />
    </div>
  ) : (
    <div className={styles.FilterItem}>
      <InputLabel for={filter.key}>{filter.label}</InputLabel>
      <Dropdown
        id={filter.key}
        placeholder={"All"}
        items={filter.options || []}
        onSelect={(value) => {
          onFilterChange(
            produce(filterState, (draft) => {
              if (filter.custom) {
                if (!draft.custom_tags_filter) {
                  draft.custom_tags_filter = [];
                }
                const existing = draft.custom_tags_filter.find(
                  (tag) => tag.key === filter.key,
                );
                if (existing) {
                  existing.values = value;
                } else {
                  draft.custom_tags_filter.push({
                    key: filter.key,
                    values: value,
                  });
                }
                return;
              }
              (draft as Record<string, string[]>)[filter.key] = value;
            }),
          );
        }}
        selected={selected}
        multiple
        searchable={filter.key !== "contextual_relevancy"}
        sorting={
          filter.key !== "contextual_relevancy" ? "alphabetical" : undefined
        }
      />
    </div>
  );
};
